import queryString, { StringifiableRecord } from 'query-string';
import { compile, pathToRegexp, Key } from 'path-to-regexp';
import React from 'react';
import { crabDevice } from '@hfzq/crab-utils';
import { xfnRequest } from '@src/apis/helper/request-token';

/**
 * 解析路由query
 *
 * @param str 需要解析的字符串
 * @returns 返回指定类型的对象
 */
export function parseQuery<T extends Record<string, unknown>>(str: string): T {
  return queryString.parse(str) as unknown as T;
}

/**
 * 向url后面拼接参数
 */
export function urlAddQuery(url: string, query?: StringifiableRecord) {
  const { url: urlStr, query: queryParams } = queryString.parseUrl(url);
  return queryString.stringifyUrl({
    url: urlStr,
    query: {
      ...queryParams,
      ...query,
    },
  });
}

/**
 * 将请求参数转换成query字符串
 * 并且会根据字符串是否为空自动拼接？号
 *
 * @param params 键是string, 值是 string 或 string数组的对象
 * @returns string
 */
export function queryParams2String(params?: StringifiableRecord): string {
  if (!params) {
    return '';
  }
  const str = queryString.stringify(params);
  return str && `?${str}`;
}

/**
 *
 * 根据参数补全路径， 同时返回剩余的参数
 * compilePath('/api/:id', {id:'10', name:'jason'}) =>  {path:'/api/10', {name:'jason'}}
 *
 * @param path api地址或者路由地址
 * @param params StringifiableRecord
 * @returns 返回路径和剩余参数
 */
export function compilePath(
  path: string,
  params: StringifiableRecord = {}
): { path: string; params: StringifiableRecord; search: string } {
  const paramsCopy = { ...params };
  const toPath = compile(path, { encode: encodeURIComponent });
  const keys: Key[] = [];
  pathToRegexp(path, keys);
  keys.forEach(({ name }) => {
    const valType = typeof paramsCopy[name];
    if (!['string', 'number'].includes(valType)) {
      throw new Error(`${path} 无法匹配参数， 请确认参数${name}的类型是string或number值`);
    }
    delete paramsCopy[name];
  });
  return {
    path: toPath(params),
    search: queryParams2String(paramsCopy),
    params: paramsCopy,
  };
}

/**
 * 判断对象是否是 plain object
 *
 * @param obj 需要判断的数据
 */
export function isPlainObject(obj: unknown): boolean {
  if (Object.prototype.toString.call(obj) !== '[object Object]') {
    return false;
  }

  const prototype = Object.getPrototypeOf(obj);
  return prototype === null || prototype === Object.prototype;
}

/**
 *  判断对象是否为空
 *
 * @param obj 需要判断的对象
 * @returns 如果是空对象则返回true
 */
export function isEmptyObject(obj: unknown): boolean {
  if (!isPlainObject(obj)) {
    throw new Error('请确认您传入的对象是 plain object');
  }
  return Object.keys(obj as Record<string, unknown>).length === 0;
}

/**
 * 等待一定时间返回传入的数据
 * @param result 需要返回的数据
 * @param time 需要等待的时间
 */
export function wait<T>(result?: T, time = 2000): Promise<T | undefined> {
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve(result);
    }, time);
  });
}

/**
 *
 * 将px单位转rem
 *
 * @param px 需要转的px数值
 */
export function px2Rem(px: number): string {
  return `${px / 37.5}rem`;
}

/**
 * 创建 context
 *
 * @param defaultValue
 */
export function createCtx<A>(defaultValue: A) {
  type UpdateType = React.Dispatch<React.SetStateAction<typeof defaultValue>>;
  const defaultUpdate: UpdateType = () => defaultValue;
  const ctx = React.createContext({
    state: defaultValue,
    update: defaultUpdate,
  });
  // eslint-disable-next-line @typescript-eslint/ban-types
  const Provider = (props: React.PropsWithChildren<{}>) => {
    const [state, update] = React.useState(defaultValue);
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    return <ctx.Provider value={{ state, update }} {...props} />;
  };
  return [ctx, Provider] as const;
}

/**
 * 判断当前环境是否是生产
 *
 * 只能区分生产和开发
 */
export function isDev() {
  return process.env.NODE_ENV === 'development';
}

/**
 * 判断当前环境是否是鸿蒙
 */
export function isHarmonyApp() {
  return crabDevice.isHarmony && crabDevice.isTZT;
}
// eslint-disable-next-line require-jsdoc
export function detectFoldableDevice(limitWidth = 750) {
  const { width } = window.screen;
  return width >= limitWidth;
}
type StringifyAll<T> = {
  [K in keyof T]: string;
};
export const getServerTime = async () => {
  try {
    const res = await xfnRequest.getXfnData({
      action: '5',
    });
    const date = res.TIME.split(' ')[0].split('-');
    const data = {
      year: date[0],
      month: date[1],
      day: date[2],
    };
    return data as StringifyAll<typeof data>;
  } catch (error) {
    const date = new Date();
    return {
      year: `${date.getFullYear()}`,
      month: `${date.getMonth() + 1}`,
      day: `${date.getDate()}`,
    };
  }
};
